import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  clearAdminStoreViewState,
  normalizeAdminReturnPath,
  readAdminStoreViewState,
  writeAdminStoreViewState,
} from '../lib/adminStoreViewState';

const AdminStoreViewContext = createContext(null);

export function AdminStoreViewProvider({ children }) {
  const { user, loading } = useAuth();
  const [storeView, setStoreView] = useState(readAdminStoreViewState);
  const entryPendingRef = useRef(false);

  const exitStoreView = useCallback(() => {
    entryPendingRef.current = false;
    setStoreView(clearAdminStoreViewState());
  }, []);

  const enterStoreView = useCallback((returnPath = '/admin') => {
    if (user?.role !== 'admin') return false;
    entryPendingRef.current = true;
    const nextState = writeAdminStoreViewState({
      enabled: true,
      returnPath: normalizeAdminReturnPath(returnPath),
    });
    setStoreView(nextState);
    return true;
  }, [user]);

  const completeStoreViewEntry = useCallback(() => {
    entryPendingRef.current = false;
  }, []);

  const isStoreViewEntryPending = useCallback(
    () => entryPendingRef.current,
    []
  );

  useEffect(() => {
    if (!loading && user?.role !== 'admin' && storeView.enabled) {
      exitStoreView();
    }
  }, [exitStoreView, loading, storeView.enabled, user]);

  const isStoreView = user?.role === 'admin' && storeView.enabled;
  const value = useMemo(() => ({
    isStoreView,
    returnPath: storeView.returnPath,
    enterStoreView,
    exitStoreView,
    completeStoreViewEntry,
    isStoreViewEntryPending,
  }), [
    completeStoreViewEntry,
    enterStoreView,
    exitStoreView,
    isStoreView,
    isStoreViewEntryPending,
    storeView.returnPath,
  ]);

  return (
    <AdminStoreViewContext.Provider value={value}>
      {children}
    </AdminStoreViewContext.Provider>
  );
}

export function useAdminStoreView() {
  const context = useContext(AdminStoreViewContext);
  if (!context) {
    throw new Error('useAdminStoreView must be used within AdminStoreViewProvider');
  }
  return context;
}
